import * as rxjs from "rxjs";
import * as rxjsOperators from "rxjs/operators";
import { option } from "fp-ts/lib";
import * as fetchWrapper from "../../../wrappers/fetch";
import * as ListingSuccessResponse2 from "../../../../../domain/models/ListingSuccessResponse2";
import * as ListingStatusCountsResponse1 from "../../../../../domain/models/ListingStatusCountsResponse1";
import * as FirstPartyFetchResponse from "../../../../../domain/models/FirstPartyFetchResponse";
import * as ListingFilter1 from "../../../../../domain/models/ListingFilter1";
import * as util from "../../../util";
import * as TCRMListings from "../../TCRMListings";
import { TActionObservable } from "../../applyActions";
import { TGetState } from "../../TGetState";
import { TSetState } from "../../TSetState";
import { TActionsDefinitionsList } from "../TAction";
import { TChangeRouteAction } from "../../router/routerTypes";

const PAGE_SIZE = 200;

export const actions: TActionsDefinitionsList = [
    {
        type: "VIEW_CRM_LISTINGS",
        run: (
            obs$: rxjs.Observable<TChangeRouteAction<"VIEW_CRM_LISTINGS">>,
            getState: TGetState,
            setState: TSetState,
        ): void => {
            obs$.pipe(
                rxjsOperators.tap(() =>
                    setState(({...s}) => {
                        s.activeData.crm.listings = new TCRMListings.C();
                        return s;
                    }),
                ),
                rxjsOperators.mergeMap(() =>
                    rxjs.forkJoin([
                        getStatusCounts(setState),
                        getListings(getState, setState),
                    ]),
                ),
            ).subscribe();
        },
    },
    {
        type: "CRM_LISTINGS_SELECT_STATUS",
        run: (
            obs$: TActionObservable<"CRM_LISTINGS_SELECT_STATUS", { status: TCRMListings.TSelectedStatus }>,
            getState: TGetState,
            setState: TSetState,
        ): void => {
            obs$.pipe(
                rxjsOperators.tap((action) =>
                    setState(({...s}) => {
                        s.activeData.crm.listings.selectedStatus = action.payload.status;
                        return s;
                    }),
                ),
                rxjsOperators.switchMap(() => getListings(getState, setState)),
            ).subscribe();
        },
    },
    {
        type: "CRM_LISTINGS_SEARCH_CHANGED",
        run: (
            obs$: TActionObservable<"CRM_LISTINGS_SEARCH_CHANGED", string>,
            getState: TGetState,
            setState: TSetState,
        ): void => {
            obs$.pipe(
                rxjsOperators.tap(({payload}) =>
                    setState(({...s}) => {
                        s.activeData.crm.listings.addressSearch = payload;
                        return s;
                    }),
                ),
                rxjsOperators.debounceTime(250),
                rxjsOperators.switchMap(() => getListings(getState, setState)),
            ).subscribe();
        },
    },
];

const fetchListingsPage = (getState: TGetState, offset: number): rxjs.Observable<FirstPartyFetchResponse.T<ListingSuccessResponse2.T>> => {
    const { selectedStatus, addressSearch } = getState().activeData.crm.listings;

    const filters = ListingFilter1.createForListView(
        selectedStatus === "all" ? null : selectedStatus,
        addressSearch,
    );

    return rxjs.from(
        fetchWrapper.json({
            requestParams: {
                url: `${env.REACT_APP_API_URL}/v1/listings?limit=${PAGE_SIZE}&offset=${offset}${filters ? `&filters=${encodeURIComponent(JSON.stringify(filters))}` : ""}`,
                method: "GET",
                body: option.none,
            },
            expectedTypeCodec: ListingSuccessResponse2.codec,
            defaultResponse: ListingSuccessResponse2.newDefault(),
        })(),
    );
};

const getListings = (getState: TGetState, setState: TSetState): rxjs.Observable<FirstPartyFetchResponse.T<ListingSuccessResponse2.T>> =>
    fetchListingsPage(getState, 0)
        .pipe(
            rxjsOperators.expand((response) => {
                const meta = response.response.meta;
                const loaded = meta.offset + meta.returned_count;
                return response.tag === FirstPartyFetchResponse.constants.STATUS_2XX && loaded < meta.available_count
                    ? fetchListingsPage(getState, loaded)
                    : rxjs.EMPTY;
            }),
            rxjsOperators.tap(util.defaultCRMRequestErrorHandler),
            rxjsOperators.filter((response) => response.tag === FirstPartyFetchResponse.constants.STATUS_2XX),
            rxjsOperators.tap((response) =>
                setState(({...s}) => {
                    const existing = s.activeData.crm.listings.results.meta;
                    const isFirstPage = response.response.meta.offset === 0;
                    const data = isFirstPage
                        ? response.response.data
                        : existing.data.concat(response.response.data);

                    s.activeData.crm.listings.results.meta = {
                        data,
                        meta: {
                            ...response.response.meta,
                            offset: 0,
                            returned_count: data.length,
                        },
                    };
                    return s;
                })
            ),
        );

const getStatusCounts = (setState: TSetState): rxjs.Observable<FirstPartyFetchResponse.T<ListingStatusCountsResponse1.T>> =>
    rxjs.from(
        fetchWrapper.json({
            requestParams: {
                url: `${env.REACT_APP_API_URL}/v1/listings/status-counts`,
                method: "GET",
                body: option.none,
            },
            expectedTypeCodec: ListingStatusCountsResponse1.codec,
            defaultResponse: ListingStatusCountsResponse1.newDefault(),
        })(),
    )
        .pipe(
            rxjsOperators.tap(util.defaultCRMRequestErrorHandler),
            rxjsOperators.filter((response) => response.tag === FirstPartyFetchResponse.constants.STATUS_2XX),
            rxjsOperators.tap((response) =>
                setState(({...s}) => {
                    s.activeData.crm.listings.statusCounts = response.response.data;
                    return s;
                })
            ),
        );
